/**
 * Data Aggregation Utilities
 *
 * Functions for aggregating time series data at different frequencies
 * (timestep, hourly, daily, monthly, annual).
 */

import type { DataPoint, ReportFrequency, TimeSeries, Timestamp } from '@/simulation/parsers/types';

/**
 * Aggregation method
 */
export type AggregationMethod = 'sum' | 'average' | 'min' | 'max';

/**
 * Aggregation frequency
 */
export type AggregationFrequency = 'timestep' | 'hourly' | 'daily' | 'monthly' | 'annual';

/**
 * Aggregated data point
 */
export interface AggregatedDataPoint {
  /** Label for the aggregated period */
  label: string;
  /** Aggregated value */
  value: number;
  /** Number of data points aggregated */
  count: number;
  /** Original timestamp info (first in period) */
  timestamp?: Timestamp;
  /** Month (1-12) for monthly/annual aggregations */
  month?: number;
  /** Day (1-31) for daily aggregations */
  day?: number;
  /** Hour (0-23) for hourly aggregations */
  hour?: number;
}

/**
 * Month names for labels
 */
const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Get a key for grouping data points by hour
 */
function getHourlyKey(ts: Timestamp): string {
  return `${String(ts.month)}-${String(ts.day)}-${String(ts.hour)}`;
}

/**
 * Get a key for grouping data points by day
 */
function getDailyKey(ts: Timestamp): string {
  return `${String(ts.month)}-${String(ts.day)}`;
}

/**
 * Get a key for grouping data points by month
 */
function getMonthlyKey(ts: Timestamp): string {
  return String(ts.month);
}

/**
 * Aggregate values using the specified method
 */
function aggregate(values: number[], method: AggregationMethod): number {
  if (values.length === 0) return 0;

  switch (method) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'average':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return values.reduce((a, b) => a + b, 0);
  }
}

/**
 * Convert raw timestep data to aggregated format (no aggregation, pass-through)
 */
export function aggregateToTimestep(
  data: DataPoint[]
): AggregatedDataPoint[] {
  return data.map((point) => {
    const ts = point.timestamp;
    const label = `${MONTH_NAMES[ts.month - 1] ?? ''} ${String(ts.day)} ${String(ts.hour).padStart(2, '0')}:${String(ts.minute).padStart(2, '0')}`;
    return {
      label,
      value: point.value,
      count: 1,
      timestamp: ts,
      month: ts.month,
      day: ts.day,
      hour: ts.hour,
    };
  });
}

/**
 * Aggregate time series data to hourly frequency
 */
export function aggregateToHourly(
  data: DataPoint[],
  method: AggregationMethod = 'average'
): AggregatedDataPoint[] {
  const groups = new Map<string, { values: number[]; timestamp: Timestamp }>();

  for (const point of data) {
    const key = getHourlyKey(point.timestamp);
    const group = groups.get(key);
    if (group) {
      group.values.push(point.value);
    } else {
      groups.set(key, { values: [point.value], timestamp: point.timestamp });
    }
  }

  const results: AggregatedDataPoint[] = [];
  for (const [, group] of groups) {
    const ts = group.timestamp;
    results.push({
      label: `${MONTH_NAMES[ts.month - 1] ?? ''} ${String(ts.day)} ${String(ts.hour)}:00`,
      value: aggregate(group.values, method),
      count: group.values.length,
      timestamp: ts,
      month: ts.month,
      day: ts.day,
      hour: ts.hour,
    });
  }

  // Sort by timestamp
  return results.sort((a, b) => {
    if (a.month !== b.month) return (a.month ?? 0) - (b.month ?? 0);
    if (a.day !== b.day) return (a.day ?? 0) - (b.day ?? 0);
    return (a.hour ?? 0) - (b.hour ?? 0);
  });
}

/**
 * Aggregate time series data to daily frequency
 */
export function aggregateToDaily(
  data: DataPoint[],
  method: AggregationMethod = 'average'
): AggregatedDataPoint[] {
  const groups = new Map<string, { values: number[]; timestamp: Timestamp }>();

  for (const point of data) {
    const key = getDailyKey(point.timestamp);
    const group = groups.get(key);
    if (group) {
      group.values.push(point.value);
    } else {
      groups.set(key, { values: [point.value], timestamp: point.timestamp });
    }
  }

  const results: AggregatedDataPoint[] = [];
  for (const [, group] of groups) {
    const ts = group.timestamp;
    results.push({
      label: `${MONTH_NAMES[ts.month - 1] ?? ''} ${String(ts.day)}`,
      value: aggregate(group.values, method),
      count: group.values.length,
      timestamp: ts,
      month: ts.month,
      day: ts.day,
    });
  }

  // Sort by timestamp
  return results.sort((a, b) => {
    if (a.month !== b.month) return (a.month ?? 0) - (b.month ?? 0);
    return (a.day ?? 0) - (b.day ?? 0);
  });
}

/**
 * Aggregate time series data to monthly frequency
 */
export function aggregateToMonthly(
  data: DataPoint[],
  method: AggregationMethod = 'average'
): AggregatedDataPoint[] {
  const groups = new Map<string, { values: number[]; timestamp: Timestamp }>();

  for (const point of data) {
    const key = getMonthlyKey(point.timestamp);
    const group = groups.get(key);
    if (group) {
      group.values.push(point.value);
    } else {
      groups.set(key, { values: [point.value], timestamp: point.timestamp });
    }
  }

  const results: AggregatedDataPoint[] = [];
  for (const [, group] of groups) {
    const ts = group.timestamp;
    results.push({
      label: MONTH_NAMES[ts.month - 1] ?? '',
      value: aggregate(group.values, method),
      count: group.values.length,
      timestamp: ts,
      month: ts.month,
    });
  }

  // Sort by month
  return results.sort((a, b) => (a.month ?? 0) - (b.month ?? 0));
}

/**
 * Aggregate time series data to annual frequency (single value)
 */
export function aggregateToAnnual(
  data: DataPoint[],
  method: AggregationMethod = 'sum'
): AggregatedDataPoint {
  const values = data.map((d) => d.value);
  return {
    label: 'Annual',
    value: aggregate(values, method),
    count: values.length,
  };
}

/**
 * Aggregate time series data at the specified frequency
 */
export function aggregateTimeSeries(
  data: DataPoint[],
  frequency: AggregationFrequency,
  method: AggregationMethod = 'average'
): AggregatedDataPoint[] {
  switch (frequency) {
    case 'timestep':
      return aggregateToTimestep(data);
    case 'hourly':
      return aggregateToHourly(data, method);
    case 'daily':
      return aggregateToDaily(data, method);
    case 'monthly':
      return aggregateToMonthly(data, method);
    case 'annual':
      return [aggregateToAnnual(data, method === 'average' ? 'sum' : method)];
    default:
      return aggregateToMonthly(data, method);
  }
}

/**
 * Get suggested aggregation method based on variable units
 */
export function getSuggestedAggregationMethod(units: string): AggregationMethod {
  // Energy units should be summed
  if (['J', 'kJ', 'MJ', 'GJ', 'Wh', 'kWh', 'MWh'].includes(units)) {
    return 'sum';
  }
  // Rate/power units should be averaged
  if (['W', 'kW', 'MW'].includes(units)) {
    return 'average';
  }
  // Temperature should be averaged
  if (units === 'C' || units === 'K' || units === 'F') {
    return 'average';
  }
  // Default to average
  return 'average';
}

/**
 * Get statistics for a time series
 */
export function getTimeSeriesStats(data: DataPoint[]): {
  min: number;
  max: number;
  mean: number;
  sum: number;
  count: number;
  minTimestamp: Timestamp | undefined;
  maxTimestamp: Timestamp | undefined;
} {
  if (data.length === 0) {
    return { min: 0, max: 0, mean: 0, sum: 0, count: 0, minTimestamp: undefined, maxTimestamp: undefined };
  }

  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let minTimestamp: Timestamp | undefined;
  let maxTimestamp: Timestamp | undefined;

  for (const point of data) {
    if (point.value < min) {
      min = point.value;
      minTimestamp = point.timestamp;
    }
    if (point.value > max) {
      max = point.value;
      maxTimestamp = point.timestamp;
    }
    sum += point.value;
  }

  return {
    min,
    max,
    mean: sum / data.length,
    sum,
    count: data.length,
    minTimestamp,
    maxTimestamp,
  };
}

/**
 * Determine the native frequency of data based on timestamps
 */
export function detectFrequency(data: DataPoint[]): ReportFrequency {
  if (data.length < 2) return 'timestep';

  // Check interval between first two points
  const t1 = data[0]?.timestamp;
  const t2 = data[1]?.timestamp;

  if (!t1 || !t2) return 'timestep';

  // Same month and day, different hour = hourly or sub-hourly
  if (t1.month === t2.month && t1.day === t2.day) {
    if (t1.hour === t2.hour) {
      return 'timestep';
    }
    return 'hourly';
  }

  // Same month, different day = daily
  if (t1.month === t2.month) {
    return 'daily';
  }

  // Different month = monthly
  return 'monthly';
}

/**
 * Convert timestep data to chart-friendly format with Date objects
 */
export function toChartData(
  timeSeries: TimeSeries,
  frequency: AggregationFrequency = 'monthly'
): Array<{ name: string; value: number; timestamp: Timestamp | undefined }> {
  const aggregated = aggregateTimeSeries(
    timeSeries.data,
    frequency,
    getSuggestedAggregationMethod(timeSeries.variable.units)
  );

  return aggregated.map((point) => ({
    name: point.label,
    value: point.value,
    timestamp: point.timestamp,
  }));
}
